import React from 'react';
import { ApplicationManagerClient } from 'clients/application-manager-client';
import { Button, Grid, Modal, Typography } from '@mui/material';
import { CircularLoadingButton } from 'components/circular-loading-button/circular-loading-button';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { getModalStyle } from 'services/utils/styles-service';
import { nameOfFactory } from 'utils/types-util';
import { notifyError, notifySuccess } from 'actions/action-notifications';
import { useDispatch } from 'react-redux';
import { IState } from 'interfaces/redux/IState';
import { logger } from 'winston-logger';
import { useTypedSelector } from 'hooks/use-typed-selector';
import Validation from 'components/form/validation/validation';
import { buildQaId } from 'utils/build-qa-id';
import { IRole } from 'interfaces/redux/ILookups';
import { ReactSelect } from 'components/form/field/react_select';

import { UserUtils } from 'utils/user-utils';
import { UserPermissions } from 'interfaces/user/Permissions';
import { NumberOption, StringOption } from 'interfaces/forms/types';
import LoadingOverlay from 'components/loading-overlay/loading-overlay';
import { ControlledText } from 'components/react-hook-form-fields';
import CustomerDropdownRaw from 'containers/application-manager/components/customer-dropdown-raw';
import { addDataCollectItem, editDataCollectItem, fetchSmsTemplates } from 'actions/action-lookups';
import { IResult } from '../tables/types';
import { styles } from './add-new-sms-template-modal.styles';
import {
  IPayloadData,
  IPayloadObject,
  ISmsTemplateItem,
  IEditSmsTemplateItem,
  IEditPayloadObject,
  ITagTypeIdItem,
} from './types';
import { numberParser } from '../utils';

interface IAddNewSmsTemplateModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
  initialValue?: any;
  categoryOptionsList?: NumberOption[];
  editing: boolean;
}

interface IFormFields {
  name?: string;
  content?: string;
  resource_type?: { label: string; value: string }[];
  id?: number | null | undefined;
}

interface IFormFieldsForDefaultValues {
  name?: string;
  content?: string;
  resource_type?: ITagTypeIdItem[];
  id?: number | null | undefined;
}

const formFieldsValues: IFormFieldsForDefaultValues = {
  name: '',
  content: '',
  resource_type: [],
  id: null,
};

const getFieldName = nameOfFactory<IFormFields>();
const requiredErrorMsg = 'Required';
const positiveNumber = 'Missing decimal values';
const postiviveNumberRequired = 'Number should be greater than 0';
const getQaId = buildQaId('application-manager.add-new-sms-template-modal', '.');

export const AddNewSmsTemplateModal: React.FC<IAddNewSmsTemplateModalProps> = (
  props: IAddNewSmsTemplateModalProps,
): JSX.Element => {
  const classes: any = styles();

  const tagTypeLookup = useTypedSelector((state: IState) => state.lookups.patientNotesTagTypes);
  const customers = useTypedSelector((state: IState) => state.filters.customers);
  const selectedCustomerId = useTypedSelector((state: IState) => state.filters.selectedCustomerId);
  const user = useTypedSelector((state: IState) => state.auth?.currentUser);
  const { roles, permissions } = useTypedSelector((state: IState) => state.auth.currentUser);
  const categoryArr =
    tagTypeLookup && tagTypeLookup.length > 0
      ? tagTypeLookup.map(t => ({
          label: t.tag_type,
          value: t.id,
        }))
      : [];

  const allCustomers = customers.map<NumberOption>(x => ({
    label: x.name,
    value: x.id,
  }));

  const initialCustomer =
    props.editing && props.initialValue && props.initialValue.site_customer_id
      ? [allCustomers.find(c => c.value === props.initialValue.site_customer_id)]
      : [];

  const userCanEdit = UserUtils.userIsPermitted(UserPermissions.ApplicationManagerSmsTemplateEdit);

  const canEditCustomer = () => {
    if (userCanEdit) {
      return true;
    }
    if (initialCustomer.length > 0 || selectedCustomerIds.length > 0) {
      const templateCustomerObj =
        selectedCustomerIds.length === 0 ? initialCustomer : selectedCustomerIds;
      if (templateCustomerObj && templateCustomerObj[0] && templateCustomerObj[0].value) {
        if (templateCustomerObj.length > 1) {
          return false;
        }
        const templateCustomerId = templateCustomerObj[0].value;
        return templateCustomerId === selectedCustomerId;
      }
    }
    return false;
    // return workingCustomerId === selectedCustomerId;
  };

  // #region component state
  const form = useForm<any>({});
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [valueType, setValueType] = React.useState<StringOption>();
  const [selectedCustomerIds, setSelectedCustomerIds] = React.useState<any>(initialCustomer);
  const [formValues, setFormValues] = React.useState<IFormFieldsForDefaultValues>(
    props.initialValue
      ? { ...props.initialValue, resource_type: [props.initialValue.resource_type] }
      : formFieldsValues,
  );

  // #endregion

  // #region useEffect
  React.useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        if (props.initialValue) {
          setFormValues({
            ...props.initialValue,
            resource_type: [props.initialValue.resource_type],
          });
        }
      } catch (error) {
        logger.error(error);
        dispatch(notifyError('Error when editing sms template.'));
      } finally {
        setLoading(false);
      }
    })();
  }, [props.editing, props.initialValue]);

  // #endregion

  // #region helper functions
  const onSubmit: SubmitHandler<any> = async (formValues: IFormFields): Promise<void> => {
    await submitSmsTemplateItem(formValues);
  };

  const submitSmsTemplateItem = async (formValues: IFormFields): Promise<void> => {
    try {
      const { name } = formValues;
      const { content } = formValues;
      const { resource_type } = formValues;

      let tagTypeIdArr = [];

      if (Array.isArray(resource_type)) {
        tagTypeIdArr = resource_type;
      } else {
        const resourceTypeTag = tagTypeLookup.find(t => t.tag_type === resource_type)
          ? tagTypeLookup.find(t => t.tag_type === resource_type)?.id
          : null;
        tagTypeIdArr = [{ value: `${resourceTypeTag}` }];
      }

      let retArr: ISmsTemplateItem[] = [];

      if (
        selectedCustomerIds &&
        selectedCustomerIds.length > 0 &&
        selectedCustomerIds.length < customers.length
      ) {
        for (const tag of tagTypeIdArr) {
          const newSmsTemplateItemObject: ISmsTemplateItem = {
            content: `${content}`,
            name: `${name}`,
            tag_type_id: Number(tag?.value),
            active: true,
            id: props.editing ? props.initialValue.id : null,
            customer_id: selectedCustomerIds.map((c: any) => c.value),
            is_for_all_customers: false,
            site_version_id: props.editing ? props.initialValue.site_version_id : null,
          };
          retArr = [...retArr, newSmsTemplateItemObject];
        }
      } else {
        for (const tag of tagTypeIdArr) {
          const newSmsTemplateItemObject: ISmsTemplateItem = {
            content: `${content}`,
            name: `${name}`,
            tag_type_id: Number(tag?.value),
            active: true,
            id: props.editing ? props.initialValue.id : null,
            customer_id: [-1],
            is_for_all_customers: true,
            site_version_id: props.editing ? props.initialValue.site_version_id : null,
          };
          retArr = [...retArr, newSmsTemplateItemObject];
        }
      }

      const result = props.editing
        ? await ApplicationManagerClient.editSmsTemplate(retArr)
        : await ApplicationManagerClient.addNewSmsTemplate(retArr);

      if (result.data.success) {
        dispatch(notifySuccess('Saved'));
        dispatch(fetchSmsTemplates());
      } else {
        dispatch(notifyError(result.data.result));
      }
      form.reset();
      props.onCancel();
    } catch (error) {
      logger.error(error);
      dispatch(notifyError('Error creating SMS template'));
      form.reset();
      props.onCancel();
    }
  };

  const deleteSmsTemplateItem = async (): Promise<void> => {
    try {
      const newSmsTemplateItemObject: ISmsTemplateItem[] = [
        {
          ...props.initialValue,
          is_active: 0,
        },
      ];
      const result = await ApplicationManagerClient.editSmsTemplate(newSmsTemplateItemObject);
      if (result.data.success) {
        dispatch(notifySuccess('Template Deleted'));
        dispatch(fetchSmsTemplates());
      } else {
        dispatch(notifyError(result.data.result));
      }
      form.reset();
      props.onCancel();
    } catch (error) {
      logger.error(error);
      form.reset();
      props.onCancel();
      dispatch(notifyError('Error deleting SMS template'));
    }
  };

  // #endregion

  return (
    <Modal open={props.open} data-qa-id={getQaId('wrapper')}>
      <div style={getModalStyle()} className={classes.modal}>
        <Grid item xs={12}>
          <Grid container direction="column" spacing={2}>
            {/* Title */}
            <Grid item xs="auto">
              <Typography className={classes.title} data-qa-id={getQaId('title')}>
                {!props.editing ? 'Add SMS Template Item' : 'Edit SMS Template Item'}
              </Typography>
            </Grid>

            {/* Form */}
          </Grid>
          <Grid item xs={12} data-qa-id={getQaId('form')}>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <CustomerDropdownRaw
                  isLabelOn
                  isMulti
                  isDisabled={!!props.editing}
                  ctrlProps={{ field: { value: selectedCustomerIds } }}
                  selectedCustomerId={selectedCustomerIds}
                  setSelectedCustomerId={(customerIds: any) => setSelectedCustomerIds(customerIds)}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledText
                  name={getFieldName('name')}
                  control={form.control}
                  disabled={!!props.editing}
                  defaultValue={formValues.name}
                  validations={{ required: props.editing }}
                  percentWith={100}
                  label="Template Name *"
                  inputMetaData={{
                    touched: Boolean(form.formState.errors.typeName),
                    error: requiredErrorMsg,
                  }}
                />
              </Grid>

              <Grid item xs={4}>
                {!props.editing ? (
                  <Controller
                    name={getFieldName('resource_type')}
                    defaultValue={
                      formValues &&
                      formValues.resource_type &&
                      Array.isArray(formValues.resource_type)
                        ? formValues?.resource_type[0]?.label
                        : []
                    }
                    control={form.control}
                    rules={{ required: true }}
                    render={(ctrlProps: any) => {
                      return (
                        <>
                          <ReactSelect
                            label="Category *"
                            isDisabled={!!props.editing}
                            isMulti={!props.editing}
                            disabled={!!props.editing}
                            value={ctrlProps?.field?.value}
                            fields={categoryArr}
                            handleOnChange={(value: StringOption) => {
                              if (!props.editing) {
                                setValueType(value);
                                ctrlProps?.field?.onChange(value);
                              } else {
                                setValueType(value);
                                ctrlProps?.field?.onChange([value]);
                              }
                            }}
                            fullWidth
                          />
                          <Validation
                            touched={form.formState.errors.type}
                            error={requiredErrorMsg}
                          />
                        </>
                      );
                    }}
                  />
                ) : (
                  <>
                    <ControlledText
                      name={getFieldName('resource_type')}
                      defaultValue={
                        formValues &&
                        formValues.resource_type &&
                        formValues.resource_type.length > 0
                          ? formValues.resource_type[0].label
                          : []
                      }
                      validations={{ required: false }}
                      control={form.control}
                      percentWith={100}
                      label="Category"
                      disabled
                      inputMetaData={{
                        touched: Boolean(form.formState.errors.typeName),
                        error: requiredErrorMsg,
                      }}
                    />
                  </>
                )}
              </Grid>
              <Grid item xs={12} className={classes.contentContainer}>
                <ControlledText
                  name={getFieldName('content')}
                  control={form.control}
                  defaultValue={formValues.content}
                  validations={{ required: true }}
                  percentWith={100}
                  label="SMS Template"
                  inputMetaData={{
                    touched: Boolean(form.formState.errors.typeName),
                    error: requiredErrorMsg,
                  }}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
              <Grid item xs={2}>
                {props.editing && (
                  <Grid item>
                    <Button
                      data-qa-id={getQaId('button.cancel')}
                      onClick={() => {
                        deleteSmsTemplateItem();
                        form.reset();
                        props.onCancel();
                      }}
                    >
                      Delete
                    </Button>
                  </Grid>
                )}
              </Grid>

              <Grid item xs={10}>
                <Grid container justifyContent="flex-end" spacing={2}>
                  <Grid item>
                    <Button
                      data-qa-id={getQaId('button.cancel')}
                      onClick={() => {
                        form.reset();
                        props.onCancel();
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <CircularLoadingButton
                      data-qa-id={getQaId('button.save')}
                      buttonLabel="Save"
                      disable={!canEditCustomer()}
                      onClick={form.handleSubmit(onSubmit)}
                      isLoading={false}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </Modal>
  );
};
